import { useSearchParams, Link } from 'react-router-dom';
import { ChevronDown, X, Coins } from 'lucide-react';
import { useMarketplaceSearch, useMarketplaceEntitySearch } from '../hooks';
import {
  SearchBar,
  ArcheGrid,
  EntityGrid,
  ListingStrip,
  Pagination,
  SearchError,
} from '../components';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ArcheType, EntityType } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useOldStoreName } from '../old-store-name';

type TabValue = 'arches' | 'entities';

export function BrowsePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const oldStore = useOldStoreName();
  const query = searchParams.get('q') || '';
  const categoryId = searchParams.get('categoryId') || undefined;
  const archeType = (searchParams.get('archeType') as ArcheType) || undefined;
  const entityType = (searchParams.get('entityType') as EntityType) || undefined;
  const sortBy =
    (searchParams.get('sortBy') as 'relevance' | 'fitness' | 'popular' | 'newest') || undefined;
  const activeTab = (searchParams.get('tab') as TabValue) || 'arches';

  // Arche search
  const {
    arches,
    listings,
    loading: archesLoading,
    error: archesError,
    total: archesTotal,
    page: archesPage,
    setPage: setArchesPage,
    totalPages: archesTotalPages,
    refresh: refreshArches,
  } = useMarketplaceSearch({
    query,
    categoryId,
    archeType,
    sortBy: sortBy || (query ? 'relevance' : 'fitness'),
    limit: 20,
  });

  // Entity search
  const {
    entities,
    loading: entitiesLoading,
    error: entitiesError,
    total: entitiesTotal,
    page: entitiesPage,
    setPage: setEntitiesPage,
    totalPages: entitiesTotalPages,
    refresh: refreshEntities,
  } = useMarketplaceEntitySearch({
    query,
    categoryId,
    type: entityType,
    sortBy: sortBy || (query ? 'relevance' : 'fitness'),
    limit: 20,
  });

  const handleSearch = (newQuery: string) => {
    const params = new URLSearchParams(searchParams);
    if (newQuery.trim()) {
      params.set('q', newQuery.trim());
    } else {
      params.delete('q');
    }
    params.delete('sortBy');
    setSearchParams(params);
  };

  const handleClearSearch = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('q');
    params.delete('sortBy');
    setSearchParams(params);
  };

  const handleSortChange = (newSortBy: string) => {
    const params = new URLSearchParams(searchParams);
    if (newSortBy) {
      params.set('sortBy', newSortBy);
    } else {
      params.delete('sortBy');
    }
    setSearchParams(params);
  };

  const handleTabChange = (newTab: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', newTab);
    setSearchParams(params);
  };

  const getArcheTypeLink = (type: ArcheType | null) => {
    const params = new URLSearchParams(searchParams);
    if (type) {
      params.set('archeType', type);
    } else {
      params.delete('archeType');
    }
    params.set('tab', 'arches');
    return `/marketplace/browse?${params.toString()}`;
  };

  const getEntityTypeLink = (type: EntityType | null) => {
    const params = new URLSearchParams(searchParams);
    if (type) {
      params.set('entityType', type);
    } else {
      params.delete('entityType');
    }
    params.set('tab', 'entities');
    return `/marketplace/browse?${params.toString()}`;
  };

  const pageTitle = query
    ? `Search: "${query}"`
    : oldStore.newStoreShown
      ? 'Advanced search'
      : activeTab === 'arches'
        ? 'Browse Arches'
        : 'Browse Entities';

  return (
    <div className="bg-background">
      {/* Page Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              {oldStore.newStoreShown && (
                <Link to="/a/store" className="text-sm text-primary hover:underline">
                  ← Arche Store
                </Link>
              )}
              <h1 className="text-2xl font-bold text-foreground">{pageTitle}</h1>
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="flex-1 md:w-96">
                <SearchBar initialValue={query} onSearch={handleSearch} />
              </div>
              <Button asChild variant="outline" className="whitespace-nowrap">
                <Link to="/credits" className="flex items-center gap-1.5">
                  <Coins className="h-4 w-4" />
                  Buy Credits
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Mobile Collapsible Filter */}
          <div className="lg:hidden">
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  <span>Filters</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 space-y-4">
                {/* Arche Type Filter - only shown for arches tab */}
                {activeTab === 'arches' && (
                  <div className="bg-card rounded-lg p-4 shadow-sm border border-border">
                    <h3 className="font-semibold text-foreground mb-3 text-sm">Arche Type</h3>
                    <nav className="space-y-1">
                      <Link
                        to={getArcheTypeLink(null)}
                        className={cn(
                          'block px-3 py-2 rounded-md text-sm transition-colors',
                          !archeType
                            ? 'bg-primary text-primary-foreground font-medium'
                            : 'text-foreground hover:bg-accent',
                        )}
                      >
                        All Arches
                      </Link>
                      <Link
                        to={getArcheTypeLink('tool')}
                        className={cn(
                          'block px-3 py-2 rounded-md text-sm transition-colors',
                          archeType === 'tool'
                            ? 'bg-primary text-primary-foreground font-medium'
                            : 'text-foreground hover:bg-accent',
                        )}
                      >
                        Tools
                      </Link>
                      <Link
                        to={getArcheTypeLink('site')}
                        className={cn(
                          'block px-3 py-2 rounded-md text-sm transition-colors',
                          archeType === 'site'
                            ? 'bg-primary text-primary-foreground font-medium'
                            : 'text-foreground hover:bg-accent',
                        )}
                      >
                        Sites
                      </Link>
                    </nav>
                  </div>
                )}

                {/* Entity Type Filter - only shown for entities tab */}
                {activeTab === 'entities' && (
                  <div className="bg-card rounded-lg p-4 shadow-sm border border-border">
                    <h3 className="font-semibold text-foreground mb-3 text-sm">Entity Type</h3>
                    <nav className="space-y-1">
                      <Link
                        to={getEntityTypeLink(null)}
                        className={cn(
                          'block px-3 py-2 rounded-md text-sm transition-colors',
                          !entityType
                            ? 'bg-primary text-primary-foreground font-medium'
                            : 'text-foreground hover:bg-accent',
                        )}
                      >
                        All Entities
                      </Link>
                      <Link
                        to={getEntityTypeLink('model-definition')}
                        className={cn(
                          'block px-3 py-2 rounded-md text-sm transition-colors',
                          entityType === 'model-definition'
                            ? 'bg-primary text-primary-foreground font-medium'
                            : 'text-foreground hover:bg-accent',
                        )}
                      >
                        Models
                      </Link>
                      <Link
                        to={getEntityTypeLink('integration')}
                        className={cn(
                          'block px-3 py-2 rounded-md text-sm transition-colors',
                          entityType === 'integration'
                            ? 'bg-primary text-primary-foreground font-medium'
                            : 'text-foreground hover:bg-accent',
                        )}
                      >
                        Integrations
                      </Link>
                      <Link
                        to={getEntityTypeLink('service')}
                        className={cn(
                          'block px-3 py-2 rounded-md text-sm transition-colors',
                          entityType === 'service'
                            ? 'bg-primary text-primary-foreground font-medium'
                            : 'text-foreground hover:bg-accent',
                        )}
                      >
                        Services
                      </Link>
                    </nav>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>

          {/* Desktop Sidebar */}
          <aside className="hidden lg:block lg:w-64 flex-shrink-0 space-y-4">
            {/* Arche Type Filter - only shown for arches tab */}
            {activeTab === 'arches' && (
              <div className="bg-card rounded-lg p-4 shadow-sm border border-border">
                <h2 className="font-semibold text-foreground mb-4">Arche Type</h2>
                <nav className="space-y-1">
                  <Link
                    to={getArcheTypeLink(null)}
                    className={cn(
                      'block px-3 py-2 rounded-md text-sm transition-colors',
                      !archeType
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'text-foreground hover:bg-accent',
                    )}
                  >
                    All Arches
                  </Link>
                  <Link
                    to={getArcheTypeLink('tool')}
                    className={cn(
                      'block px-3 py-2 rounded-md text-sm transition-colors',
                      archeType === 'tool'
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'text-foreground hover:bg-accent',
                    )}
                  >
                    Tools
                    <span className="ml-2 text-muted-foreground text-xs">Work & processes</span>
                  </Link>
                  <Link
                    to={getArcheTypeLink('site')}
                    className={cn(
                      'block px-3 py-2 rounded-md text-sm transition-colors',
                      archeType === 'site'
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'text-foreground hover:bg-accent',
                    )}
                  >
                    Sites
                    <span className="ml-2 text-muted-foreground text-xs">View information</span>
                  </Link>
                </nav>
              </div>
            )}

            {/* Entity Type Filter - only shown for entities tab */}
            {activeTab === 'entities' && (
              <div className="bg-card rounded-lg p-4 shadow-sm border border-border">
                <h2 className="font-semibold text-foreground mb-4">Entity Type</h2>
                <nav className="space-y-1">
                  <Link
                    to={getEntityTypeLink(null)}
                    className={cn(
                      'block px-3 py-2 rounded-md text-sm transition-colors',
                      !entityType
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'text-foreground hover:bg-accent',
                    )}
                  >
                    All Entities
                  </Link>
                  <Link
                    to={getEntityTypeLink('model-definition')}
                    className={cn(
                      'block px-3 py-2 rounded-md text-sm transition-colors',
                      entityType === 'model-definition'
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'text-foreground hover:bg-accent',
                    )}
                  >
                    Models
                    <span className="ml-2 text-muted-foreground text-xs">AI models</span>
                  </Link>
                  <Link
                    to={getEntityTypeLink('integration')}
                    className={cn(
                      'block px-3 py-2 rounded-md text-sm transition-colors',
                      entityType === 'integration'
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'text-foreground hover:bg-accent',
                    )}
                  >
                    Integrations
                    <span className="ml-2 text-muted-foreground text-xs">External services</span>
                  </Link>
                  <Link
                    to={getEntityTypeLink('service')}
                    className={cn(
                      'block px-3 py-2 rounded-md text-sm transition-colors',
                      entityType === 'service'
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'text-foreground hover:bg-accent',
                    )}
                  >
                    Services
                    <span className="ml-2 text-muted-foreground text-xs">
                      Platform capabilities
                    </span>
                  </Link>
                </nav>
              </div>
            )}
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Active search indicator */}
            {query && (
              <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-muted rounded-lg">
                <span className="text-sm text-muted-foreground">
                  Showing results for <span className="font-medium text-foreground">"{query}"</span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 ml-1"
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-6">
              <TabsList>
                <TabsTrigger value="arches">
                  Arches ({archesLoading || archesError ? '...' : archesTotal})
                </TabsTrigger>
                <TabsTrigger value="entities">
                  Entities ({entitiesLoading || entitiesError ? '...' : entitiesTotal})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="arches" className="mt-6">
                {/* Results Header */}
                <div className="flex items-center justify-between mb-6">
                  <p className="text-muted-foreground">
                    {archesLoading
                      ? 'Loading...'
                      : archesError
                        ? 'Search unavailable'
                        : `${archesTotal} arche${archesTotal !== 1 ? 's' : ''} found`}
                  </p>

                  <div className="flex items-center gap-2">
                    <label htmlFor="sortBy" className="text-sm text-muted-foreground">
                      Sort by:
                    </label>
                    <select
                      id="sortBy"
                      value={sortBy || (query ? 'relevance' : 'fitness')}
                      onChange={(e) => handleSortChange(e.target.value)}
                      className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:ring-primary focus:border-primary"
                    >
                      {query && <option value="relevance">Relevance</option>}
                      <option value="fitness">Quality</option>
                      <option value="popular">Most Popular</option>
                      <option value="newest">Newest</option>
                    </select>
                  </div>
                </div>

                {/* Results Grid */}
                {archesError && !archesLoading ? (
                  <SearchError subject="arches" message={archesError} onRetry={refreshArches} />
                ) : (
                  <>
                    {!archesLoading && <ListingStrip listings={listings} />}
                    <ArcheGrid arches={arches} loading={archesLoading} />

                    {/* Pagination */}
                    <Pagination
                      currentPage={archesPage}
                      totalPages={archesTotalPages}
                      onPageChange={setArchesPage}
                    />
                  </>
                )}
              </TabsContent>

              <TabsContent value="entities" className="mt-6">
                {/* Results Header */}
                <div className="flex items-center justify-between mb-6">
                  <p className="text-muted-foreground">
                    {entitiesLoading
                      ? 'Loading...'
                      : entitiesError
                        ? 'Search unavailable'
                        : `${entitiesTotal} entit${entitiesTotal !== 1 ? 'ies' : 'y'} found`}
                  </p>

                  <div className="flex items-center gap-2">
                    <label htmlFor="sortByEntities" className="text-sm text-muted-foreground">
                      Sort by:
                    </label>
                    <select
                      id="sortByEntities"
                      value={sortBy || (query ? 'relevance' : 'fitness')}
                      onChange={(e) => handleSortChange(e.target.value)}
                      className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:ring-primary focus:border-primary"
                    >
                      {query && <option value="relevance">Relevance</option>}
                      <option value="fitness">Quality</option>
                      <option value="popular">Most Popular</option>
                      <option value="newest">Newest</option>
                    </select>
                  </div>
                </div>

                {/* Results Grid */}
                {entitiesError && !entitiesLoading ? (
                  <SearchError
                    subject="entities"
                    message={entitiesError}
                    onRetry={refreshEntities}
                  />
                ) : (
                  <>
                    <EntityGrid entities={entities} loading={entitiesLoading} />

                    {/* Pagination */}
                    <Pagination
                      currentPage={entitiesPage}
                      totalPages={entitiesTotalPages}
                      onPageChange={setEntitiesPage}
                    />
                  </>
                )}
              </TabsContent>
            </Tabs>
          </main>
        </div>
      </div>
    </div>
  );
}
